import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { postFileMethod } from "../../../../services/apiService";
import UploadIn from "../../../../assets/images/icon/uploadIcon.svg"
import { ToastContext, useToastContext } from "../../../../context/toastContext";

interface Data {
  name: string;
  quantity: number;
  createdTime: number;
  activationTime: number;
  expiryTime: number;
  status: number;
  licenseType: number;
  analyticType?: number;  // Optional if not all objects have it
}

interface ValidatedDataItem {
  key: string;
  partner: string;
  data: Data;
}


const tableHeaders = [
  { label: "Key", key: "key" },
  { label: "Name", key: "name" },
  { label: "Quantity", key: "quantity" },
  { label: "Created On", key: "createdTime" },
  { label: "Activation Date", key: "activationDate" },
  { label: "Expiry Date", key: "expiryDate" },
]

function PlatformImportLicense() {

  const { setToast } = useToastContext() as ToastContext;
  const [file, setFile] = useState<File | null>(null);
  const [validatedData, setValidatedData] = useState<ValidatedDataItem[]>([]);
  const onDrop = (acceptedFiles: File[]) => {setFile(acceptedFiles[0]);};
  const { getRootProps, getInputProps } = useDropzone({ onDrop });



  const handleFileUpload = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append("license", file);

    const response: any = await postFileMethod("platform/license/validate", formData);
    if (response?.status === 200) {
      setToast(response?.data?.message, "success");
      setValidatedData(response?.data?.result)
    } else {
      setToast(response?.data?.message, "error");
    }
  };



  useEffect(() => {
    handleFileUpload()
  }, [file]);



  const handleApply = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append("license", file);

    const response: any = await postFileMethod("platform/license", formData)
    if (response?.status === 200) {
      setToast("License(s) imported successfully!", "success");
      setFile(null);
      setValidatedData([]);
    } else {
      setToast(response?.data?.message, "error");
    }
  }



  const handleCancel = () => {
    setValidatedData([]);
    setFile(null);
  }



  const convertTimestampToDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    const formattedDate = date.toLocaleDateString("en-GB", {
      day: "2-digit",     
      month: "short",
      year: "numeric",
    });

    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "2-digit",    
      minute: "2-digit",  
      second: "2-digit", 
      hour12: true,
    });

    return `${formattedDate} | ${formattedTime}`;
  };



  return (
    <>
      <Box sx={{ display: "flex", alignContent: "space-between", justifyContent: "space-between", gap: "32px", padding: "20px 0" }}>
        <div style={{
          width: "100%",
          textAlign: "center",
        }}>
          {/* Drag and drop or browse */}
          <div
            {...getRootProps()}
            style={{
              border: "1px dashed #ccc",
              width: "100%",
              height: "120px",
              marginBottom: "20px",
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              alignContent: "center",
              alignItems: "center"
            }}
          >
            <input {...getInputProps()} />
            <><img src={UploadIn} alt="" width={24} height={24} /> <p style={{ color: "#17B2EB", marginLeft: "5px" }}> Drag files or click to browse</p></>
          </div>

          {/* If a file is selected, show the upload button */}
          {file && (
            <div>
              <p>Selected file: {file.name}</p>
            </div>
          )}
        </div>
      </Box>
      <Box sx={{ marginBottom: "10px" }}>
        <h2 style={{ fontSize: "15px", fontWeight: 500, color: "#E2E3E3" }}>Imports Licenses List</h2>
      </Box>
      <TableContainer
        component={Paper}
        sx={{ backgroundColor: "#192529", padding: "1rem" }}
      >
        <Table sx={{ backgroundColor: "#192529", border: "1px solid #313838" }}>
          {/* Table Header */}
          <TableHead>
            <TableRow style={{ borderBottom: "2px solid #313838" }}>
              {tableHeaders.map((header) => (
                <TableCell
                  key={header.key}
                  sx={{
                    color: "#E2E3E3",
                    borderColor: "#313838",
                    fontWeight: "500",
                    fontSize: "16px",
                  }}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* Table Body */}
          <TableBody>
            {validatedData && validatedData.length > 0 ? (
              validatedData?.map((row, index) => (
                <TableRow key={index}>
                  {/* Data Columns */}
                  <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}>
                    {row.key}
                  </TableCell>

                  <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}>
                    {row.data.name}
                  </TableCell>

                  <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}>
                    {row.data.quantity}
                  </TableCell>

                  <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}>
                    {convertTimestampToDate(row.data.createdTime)}
                  </TableCell>

                  <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}>
                    {convertTimestampToDate(row.data.activationTime)}
                  </TableCell>

                  <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}>
                    {convertTimestampToDate(row.data.expiryTime)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              // Display a row with "-" if no data exists
              <TableRow>
                <TableCell
                  colSpan={6}
                  sx={{
                    textAlign: "center",
                    color: "#B4B7B8",
                    borderColor: "#313838",
                  }}
                >
                  {"-"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>


      {validatedData && validatedData.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", padding: "0 20px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 3,
            }}
          >
            <button style={{ cursor: "pointer" }} className="add-btn" onClick={handleCancel} >
              Cancel
            </button>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 3,
            }}
          >
            <button style={{ cursor: "pointer" }} className="add-btn" onClick={handleApply} >
              Apply
            </button>
          </Box>
        </div>
      )}
    </>
  )
}

export default PlatformImportLicense